import { Injectable } from '@angular/core';
import {
  AuthAccessTokenDetails,
  OauthAEMConfig
} from '@com-delta-idp/mnx-oauth';
import {
  AEMErrorMessagesSection,
  AEMFeatureFlags,
  CommonConstants,
  EndpointKeys,
  IDP_APPS,
  Libraries
} from '@com-delta-idp/mnx-skymiles-shared';
import {
  ApiBuilder,
  HttpHeadersPayload,
  HttpRequestMethods
} from '@com-delta-idp/mnx-utils';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AEMMedallionBannerContent } from '../models/aem/medallion-banner.interfaces';
import { AEMMenuContent } from '../models/aem/menu.interfaces';
import { FeaturesFlag } from '../models/business-traveler.interfaces';
import { Features } from '../models/features.interfaces';
import { DataHelperService } from './data-helper.service';

@Injectable({
  providedIn: 'root'
})
export class DataService extends DataHelperService {
  getMedallionBannerContent = (): Observable<AEMMedallionBannerContent> =>
    new ApiBuilder<undefined, AEMMedallionBannerContent>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_MEDALLION_BANNER'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getMenuContent = (): Observable<AEMMenuContent> =>
    new ApiBuilder<undefined, AEMMenuContent>(this.http, this.appConfig)
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_MENU'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getFeatureFlags = (): Observable<AEMFeatureFlags<Features>> =>
    new ApiBuilder<undefined, AEMFeatureFlags<Features>>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_FEATURE_FLAGS'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getErrorMessages = (): Observable<AEMErrorMessagesSection> =>
    new ApiBuilder<undefined, AEMErrorMessagesSection>(
      this.http,
      this.appConfig
    )
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_LANDING_PAGE_ERROR_MESSAGES'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create();

  getBusinessTravelerFeatureFlagAEMContent = (): Observable<boolean> =>
    new ApiBuilder<undefined, FeaturesFlag>(this.http, this.appConfig)
      .app(IDP_APPS.SKYMILES)
      .endpointKey(EndpointKeys['SKYMILES_AEM_BUSINESS_TRAVELER_FEATURE_FLAGS'])
      .method(HttpRequestMethods['GET'])
      .timeOut(CommonConstants.TIMEOUT)
      .errorHandler(this.errorHandler)
      .library(Libraries.LANDING_PAGE)
      .create()
      .pipe(
        map((response: FeaturesFlag) => {
          return !!response?.flags?.overview;
        })
      );

  getBusinessTravelerContent = (): Observable<boolean> => {
    const headers: HttpHeadersPayload = {
      'Content-Type': 'application/json'
    };

    if (this.session.token?.access_token) {
      headers['Authorization'] = `Bearer ${this.session.token.access_token}`;
      return this.createBusinessTravelerRequest(headers);
    } else {
      return this.userData.getAuthConfigContent().pipe(
        switchMap((res: OauthAEMConfig) => {
          const authCopy: OauthAEMConfig = {
            ...res,
            SCOPE_VALUE_RM: `${res.SCOPE_VALUE} ${res.SAVED_SEARCH_GRAPHQL_SCOPE_VALUE}`,
            CLIENT_ID_VALUE: res['HEADER_CLIENT_ID_VALUE'] || ''
          };
          return this.appToken.getAppToken(authCopy).pipe(
            switchMap((token: AuthAccessTokenDetails) => {
              headers['Authorization'] = `Bearer ${token.access_token}`;
              return this.createBusinessTravelerRequest(headers);
            })
          );
        })
      );
    }
  };
}
